import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) toast.success("Welcome back!");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand side */}
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A3D91] via-[#0E73C9] to-[#1CC7B3]" />
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }}
        />
        <div className="relative z-10 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo-harmony.png" className="h-12 w-12 bg-white rounded-xl p-1" alt="HARMONY" />
            <div>
              <div className="font-display font-bold text-2xl tracking-tight">HARMONY</div>
              <div className="text-xs uppercase tracking-[0.3em] opacity-80">Media Optimization</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-white max-w-md">
          <div className="text-[#A7E5FF] text-xs uppercase tracking-widest mb-3">
            Unified TV + OLV Decision Engine
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight">
            One platform.
            <br />
            Total media reach.
          </h2>
          <p className="mt-4 text-white/85 leading-relaxed text-sm">
            Combine TV GRPs and YouTube impressions into a single optimisation curve.
            Calculate combined reach, DDF, effective CPRP and budget sensitivity — all in real time.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 text-white">
            <div>
              <div className="text-3xl font-display font-bold">11+</div>
              <div className="text-xs opacity-80 mt-1">Frequency levels</div>
            </div>
            <div>
              <div className="text-3xl font-display font-bold">2</div>
              <div className="text-xs opacity-80 mt-1">Files → 1 curve set</div>
            </div>
            <div>
              <div className="text-3xl font-display font-bold">∞</div>
              <div className="text-xs opacity-80 mt-1">Scenarios</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-white/60 text-xs">© 2026 HARMONY. Enterprise media intelligence.</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/logo-harmony.png" className="h-10 w-10" alt="HARMONY" />
            <div className="font-display font-bold text-[#0A3D91] text-2xl">HARMONY</div>
          </div>

          <h1 className="font-display text-3xl font-bold text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-500 mt-2">
            Enter your credentials to access the dashboard.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Email address
              </label>
              <input
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={TID.loginEmail}
                placeholder="you@company.com"
                className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none transition"
              />
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">
                Password
              </label>
              <div className="mt-2 relative">
                <input
                  type={show ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid={TID.loginPassword}
                  placeholder="••••••••"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm pr-10 focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none transition"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="toggle-password"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={remember}
                  onCheckedChange={(v) => setRemember(!!v)}
                  data-testid={TID.loginRemember}
                />
                <span className="text-sm text-slate-700">Remember me</span>
              </label>
              <a
                href="#"
                data-testid={TID.loginForgot}
                className="text-sm text-[#0E73C9] hover:text-[#0A3D91] font-medium"
              >
                Forgot password?
              </a>
            </div>

            {error && (
              <div
                data-testid={TID.loginError}
                className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid={TID.loginSubmit}
              className="btn-primary w-full justify-center !py-2.5 text-base"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {busy ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-slate-400">
            Need an account? Contact your HARMONY administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
